import React,{useEffect,useState} from 'react';
import SafeIcon from '../../common/SafeIcon';
import {
  fetchAdminCategories,
  fetchStripeTaxMappings,
  saveAdminCategory,
  saveStripeTaxMapping
} from '../../services/stripeTaxMappingService';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertTriangle,
  FiCheckCircle,
  FiEdit2,
  FiLoader,
  FiPlus,
  FiSave
}=FiIcons;

const emptyForm={
  category_key:'',
  category_name:'',
  stripe_product_tax_code:'',
  active:true
};

const emptyCategoryForm={
  id:'',
  name:'',
  slug:'',
  icon_name:'Box',
  stripe_product_tax_code:''
};

const AdminStripeTaxMappingsPanel=({onCategorySaved})=>{
  const [mappings,setMappings]=useState([]);
  const [categories,setCategories]=useState([]);
  const [form,setForm]=useState(emptyForm);
  const [categoryForm,setCategoryForm]=useState(emptyCategoryForm);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [savingCategory,setSavingCategory]=useState(false);
  const [error,setError]=useState('');
  const [success,setSuccess]=useState('');

  const load=async()=>{
    setLoading(true);
    setError('');

    try {
      setMappings(await fetchStripeTaxMappings());
    } catch (loadError) {
      setError(loadError.message || 'Unable to load Stripe Tax mappings.');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories=async()=>{
    try {
      setCategories(await fetchAdminCategories());
    } catch (loadError) {
      setError(loadError.message || 'Unable to load marketplace categories.');
    }
  };

  useEffect(()=>{
    load();
    loadCategories();
  },[]);

  const edit=(mapping)=>{
    setForm({
      category_key:mapping.category_key,
      category_name:mapping.category_name,
      stripe_product_tax_code:mapping.stripe_product_tax_code,
      active:mapping.active
    });
    setSuccess('');
  };

  const editCategory=(category)=>{
    setCategoryForm({
      id:category.id,
      name:category.name,
      slug:category.slug,
      icon_name:category.icon_name || 'Box',
      stripe_product_tax_code:category.stripe_product_tax_code || ''
    });
    setSuccess('');
    setError('');
    window.requestAnimationFrame(()=>{
      document.getElementById('admin-category-editor')?.scrollIntoView({
        behavior:'smooth',
        block:'center'
      });
    });
  };

  const save=async(event)=>{
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const result=await saveStripeTaxMapping(form);
      setSuccess(
        `${result.affected_active_listing_count || 0} active listings were updated.`
      );
      setForm(emptyForm);
      await load();
    } catch (saveError) {
      setError(saveError.message || 'Unable to save the mapping.');
    } finally {
      setSaving(false);
    }
  };

  const saveCategory=async(event)=>{
    event.preventDefault();
    setSavingCategory(true);
    setError('');
    setSuccess('');

    try {
      const result=await saveAdminCategory(categoryForm);
      setSuccess(
        `Category saved. ${result.affected_listings || 0} listings received the tax configuration.`
      );
      setCategoryForm(emptyCategoryForm);
      await Promise.all([load(),loadCategories()]);
      onCategorySaved?.();
    } catch (saveError) {
      setError(saveError.message || 'Unable to save the category.');
    } finally {
      setSavingCategory(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-[2.5rem] bg-white p-16 text-center font-black text-indigo-600">
        <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
        Loading Stripe Tax mappings...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-2xl bg-red-50 p-4 text-sm font-bold text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center rounded-2xl bg-emerald-50 p-4 text-sm font-bold text-emerald-700">
          <SafeIcon icon={FiCheckCircle} className="mr-2" />
          {success}
        </div>
      )}

      <div className="rounded-[2.5rem] border border-indigo-100 bg-indigo-50 p-6">
        <div className="flex items-start">
          <SafeIcon icon={FiPlus} className="mr-3 mt-1 text-indigo-600" />
          <p className="text-sm font-bold leading-relaxed text-indigo-800">
            Administrators can edit marketplace categories and assign the Stripe Tax product code used for automatic tax calculation. Every category requires a valid Stripe Tax code before its listings can be used for automatic-tax checkout.
          </p>
        </div>
      </div>

      <form
        id="admin-category-editor"
        onSubmit={saveCategory}
        className="grid gap-4 rounded-[2.5rem] bg-white p-8 shadow-sm md:grid-cols-5"
      >
        <input
          required
          placeholder="Category name"
          value={categoryForm.name}
          onChange={(event)=>setCategoryForm({...categoryForm,name:event.target.value})}
          className="rounded-2xl bg-slate-50 px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <input
          required
          placeholder="URL slug, e.g. cakes"
          value={categoryForm.slug}
          onChange={(event)=>setCategoryForm({
            ...categoryForm,
            slug:event.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-')
          })}
          className="rounded-2xl bg-slate-50 px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <input
          required
          placeholder="Icon name, e.g. Coffee"
          value={categoryForm.icon_name}
          onChange={(event)=>setCategoryForm({...categoryForm,icon_name:event.target.value})}
          className="rounded-2xl bg-slate-50 px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <input
          required
          pattern="txcd_[0-9]+"
          title="Enter a Stripe Tax product code such as txcd_99999999."
          placeholder="Stripe code, e.g. txcd_99999999"
          value={categoryForm.stripe_product_tax_code}
          onChange={(event)=>setCategoryForm({
            ...categoryForm,
            stripe_product_tax_code:event.target.value.toLowerCase()
          })}
          className="rounded-2xl bg-slate-50 px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          disabled={savingCategory}
          className="flex items-center justify-center rounded-2xl bg-indigo-600 px-5 py-4 text-xs font-black uppercase tracking-widest text-white disabled:opacity-50"
        >
          <SafeIcon icon={savingCategory ? FiLoader : FiSave} className={savingCategory ? 'mr-2 animate-spin' : 'mr-2'} />
          {savingCategory ? 'Saving...' : categoryForm.id ? 'Update category' : 'Add category'}
        </button>
      </form>

      <div className="overflow-x-auto rounded-[2.5rem] bg-white shadow-sm">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">
              <th className="px-6 py-4">Marketplace category</th>
              <th className="px-6 py-4">Stripe Tax code</th>
              <th className="px-6 py-4">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {categories.map((category)=>(
              <tr key={category.id}>
                <td className="px-6 py-5">
                  <p className="font-black text-slate-900">{category.name}</p>
                  <p className="text-xs font-bold text-slate-400">
                    {category.slug} · {category.icon_name || 'Box'}
                  </p>
                </td>
                <td className="px-6 py-5 font-mono text-sm text-indigo-600">
                  {category.stripe_product_tax_code || 'Not configured'}
                </td>
                <td className="px-6 py-5">
                  <button
                    type="button"
                    onClick={()=>editCategory(category)}
                    className="flex items-center rounded-xl bg-indigo-50 px-4 py-3 text-xs font-black uppercase tracking-widest text-indigo-600 hover:bg-indigo-600 hover:text-white"
                    aria-label={`Edit ${category.name}`}
                  >
                    <SafeIcon icon={FiEdit2} className="mr-2" />
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-[2.5rem] border border-amber-100 bg-amber-50 p-6">
        <div className="flex items-start">
          <SafeIcon icon={FiAlertTriangle} className="mr-3 mt-1 text-amber-600" />
          <p className="text-sm font-bold leading-relaxed text-amber-800">
            Stripe Tax codes are administrator-only. Sellers select understandable SOHLocal categories; this mapping is applied automatically to listings and checkout.
          </p>
        </div>
      </div>

      <form onSubmit={save} className="grid gap-4 rounded-[2.5rem] bg-white p-8 shadow-sm md:grid-cols-4">
        <input
          required
          placeholder="Category key, e.g. cakes"
          value={form.category_key}
          onChange={(event)=>setForm({...form,category_key:event.target.value.toLowerCase().trim()})}
          className="rounded-2xl bg-slate-50 px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <input
          required
          placeholder="Seller-facing category name"
          value={form.category_name}
          onChange={(event)=>setForm({...form,category_name:event.target.value})}
          className="rounded-2xl bg-slate-50 px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <input
          required
          pattern="txcd_[0-9]+"
          title="Enter a Stripe Tax product code such as txcd_99999999."
          placeholder="Stripe code, e.g. txcd_99999999"
          value={form.stripe_product_tax_code}
          onChange={(event)=>setForm({...form,stripe_product_tax_code:event.target.value.toLowerCase()})}
          className="rounded-2xl bg-slate-50 px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          disabled={saving}
          className="flex items-center justify-center rounded-2xl bg-indigo-600 px-5 py-4 text-xs font-black uppercase tracking-widest text-white disabled:opacity-50"
        >
          <SafeIcon icon={saving ? FiLoader : FiSave} className={saving ? 'mr-2 animate-spin' : 'mr-2'} />
          {saving ? 'Saving...' : 'Save mapping'}
        </button>
      </form>

      <div className="overflow-x-auto rounded-[2.5rem] bg-white shadow-sm">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">
              <th className="px-6 py-4">Category</th>
              <th className="px-6 py-4">Stripe code</th>
              <th className="px-6 py-4">Active listings</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {mappings.map((mapping)=>(
              <tr key={mapping.id}>
                <td className="px-6 py-5">
                  <p className="font-black text-slate-900">{mapping.category_name}</p>
                  <p className="text-xs font-bold text-slate-400">{mapping.category_key}</p>
                </td>
                <td className="px-6 py-5 font-mono text-sm text-indigo-600">
                  {mapping.stripe_product_tax_code}
                </td>
                <td className="px-6 py-5 font-black text-slate-700">
                  {mapping.affected_active_listings || 0}
                </td>
                <td className="px-6 py-5">
                  <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase ${
                    mapping.active
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-50 text-amber-700'
                  }`}>
                    {mapping.active ? 'Active' : 'Review required'}
                  </span>
                </td>
                <td className="px-6 py-5">
                  <button
                    type="button"
                    onClick={()=>edit(mapping)}
                    className="flex items-center rounded-xl bg-slate-100 px-4 py-3 text-xs font-black uppercase tracking-widest text-indigo-600 hover:bg-indigo-600 hover:text-white"
                  >
                    <SafeIcon icon={FiEdit2} className="mr-2" />
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminStripeTaxMappingsPanel;